// AuraLogo.tsx — wordmark Aura completa (símbolo + texto).
//
// Variants:
//  - navy (default): logo escuro pra fundos claros (Login, light theme)
//  - white: logo claro pra fundos escuros (header dark, hero sections)
//
// Sizes (height-based, width auto preserva aspect ratio ~3.94:1):
//  - sm: 20px
//  - md: 32px (default)
//  - lg: 48px
//
// Asset: /brand/aura_primary_{variant}.svg (copiado de aura-tech-website em R1.2)
// Acessibilidade: por default decorativo (aria-hidden); passe ariaLabel pra
// marcar como único ID visual da página.

import type { ImgHTMLAttributes } from 'react';

import { cn } from '@/lib/utils/cn';

type AuraLogoVariant = 'navy' | 'white';
type AuraLogoSize = 'sm' | 'md' | 'lg';

interface AuraLogoProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt'> {
  variant?: AuraLogoVariant;
  size?: AuraLogoSize;
  className?: string;
  /** Aria-label. Se omitido, logo renderiza como decorativo (aria-hidden). */
  ariaLabel?: string;
}

const HEIGHT_BY_SIZE: Record<AuraLogoSize, number> = {
  sm: 20,
  md: 32,
  lg: 48,
};

export function AuraLogo({
  variant = 'navy',
  size = 'md',
  className,
  ariaLabel,
  ...rest
}: AuraLogoProps) {
  const height = HEIGHT_BY_SIZE[size];
  const decorative = !ariaLabel;

  return (
    <img
      src={`/brand/aura_primary_${variant}.svg`}
      alt={ariaLabel ?? ''}
      height={height}
      // width auto preserva ratio do SVG (viewBox 2919x740 ≈ 3.94:1)
      style={{ height, width: 'auto' }}
      className={cn('select-none', className)}
      aria-hidden={decorative}
      role={decorative ? undefined : 'img'}
      draggable={false}
      {...rest}
    />
  );
}
